import { Download, Paperclip } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Avatar } from '@/shared/components/ui/Avatar';
import { AUDIENCE_LABELS, DEPT_LABELS, attachmentUrl, isImageAttachment } from '../constants';
import type { Announcement } from '../types';

interface AnnouncementDrawerProps {
  announcement: Announcement | null;
  canManage: boolean;
  deleting?: boolean;
  onClose: () => void;
  onEdit: (a: Announcement) => void;
  onDelete: (a: Announcement) => void;
}

function fmtDate(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function AnnouncementDrawer({ announcement, canManage, deleting, onClose, onEdit, onDelete }: AnnouncementDrawerProps) {
  return (
    <Drawer open={!!announcement} onClose={onClose} title={announcement?.title ?? 'Announcement'}>
      {announcement && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="primary">{DEPT_LABELS[announcement.target_dept] ?? announcement.target_dept}</Badge>
            <Badge tone="info">{AUDIENCE_LABELS[announcement.target_audience] ?? announcement.target_audience}</Badge>
          </div>

          <div className="flex items-center gap-3">
            <Avatar name={announcement.author_name || 'Unknown'} size="md" />
            <div className="text-body-sm text-on-surface-variant">
              {announcement.author_name && <div className="text-data-md font-medium text-on-surface">{announcement.author_name}</div>}
              {fmtDate(announcement.created_at)}
            </div>
          </div>

          <p className="whitespace-pre-wrap text-data-md text-on-surface">{announcement.body}</p>

          {announcement.attachment?.file && (
            <div>
              <h3 className="mb-2 text-label-caps uppercase text-on-surface-variant">Attachment</h3>
              {isImageAttachment(announcement.attachment.type, announcement.attachment.name) ? (
                <a href={attachmentUrl(announcement.attachment.file)} target="_blank" rel="noreferrer">
                  <img
                    src={attachmentUrl(announcement.attachment.file)}
                    alt={announcement.attachment.name}
                    className="max-h-60 w-full rounded-card border border-outline object-contain"
                  />
                </a>
              ) : (
                <a
                  href={attachmentUrl(announcement.attachment.file)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between rounded-DEFAULT border border-outline bg-surface-variant px-3 py-2 text-data-md text-on-surface hover:bg-surface"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <Paperclip className="h-4 w-4 shrink-0" />
                    <span className="truncate">{announcement.attachment.name}</span>
                  </span>
                  <Download className="h-4 w-4 shrink-0" />
                </a>
              )}
            </div>
          )}

          {canManage && (
            <div className="flex gap-2 pt-2">
              <Button onClick={() => onEdit(announcement)} className="flex-1">
                Edit
              </Button>
              <Button variant="danger" loading={deleting} onClick={() => onDelete(announcement)}>
                Delete
              </Button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
